import { Video } from "lucide-react";
import { useRef, useEffect, useState } from "react";

const CameraPreview = ({
    step,
    cameraStream,
    cameraPermission,
    recordedVideoUrl,
    requestCameraAccess,
    isEditMode,
    isTextMode,
    textInput,
    selectedTextStyle,
    textOverlays,
    setTextInput,
    videoRef,
    customThumbnailPreview,
    thumbnailInputRef,
    onRemoveThumbnail,
    selectedFilter,
}) => {
    const filterStyle = selectedFilter && selectedFilter !== "none"
        ? { filter: selectedFilter }
        : {};
    const [showThumbnailControls, setShowThumbnailControls] = useState(false);


    // Update video stream when camera stream changes
    useEffect(() => {
        if (videoRef.current && cameraStream) {
            videoRef.current.srcObject = cameraStream;
        }
    }, [cameraStream]);

    // Ensure live camera feed is shown in step 1
    useEffect(() => {
        if (step === 1 && videoRef.current && cameraStream) {
            videoRef.current.srcObject = cameraStream;
        }
    }, [step, cameraStream]);

    if (cameraPermission === 'denied') {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900">
                <div className="text-center text-white p-8">
                    <h3 className="text-lg font-semibold mb-4">Camera Access Required</h3>
                    <p className="text-sm text-gray-300 mb-6">
                        Please allow camera access to record videos
                    </p>
                    <button
                        onClick={requestCameraAccess}
                        className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                        Allow Camera Access
                    </button>
                </div>
            </div>
        );
    }

    if (cameraPermission !== 'granted' || !cameraStream) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-gray-900">
                <div className="text-white text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
                    <p>Requesting camera access...</p>
                </div>
            </div>
        );
    }

    // Step 3: Small preview
    if (step === 3) {
        return recordedVideoUrl ? (
            <div
                className="relative h-[15rem] w-[9rem] mt-16 rounded-lg overflow-hidden"
                onMouseEnter={() => setShowThumbnailControls(true)}
                onMouseLeave={() => setShowThumbnailControls(false)}
            >
                <video
                    muted
                    autoPlay
                    loop
                    playsInline
                    src={recordedVideoUrl}
                    disablePictureInPicture
                    className="h-full w-full object-cover rounded-lg"
                />
                {/* Custom thumbnail overlay */}
                {customThumbnailPreview && (
                    <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none">
                        <img
                            src={customThumbnailPreview}
                            alt="thumbnail preview"
                            className="w-full h-full object-cover"
                        />
                    </div>
                )}
                {/* Thumbnail controls on hover */}
                {showThumbnailControls && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); thumbnailInputRef?.current?.click(); }}
                            className="bg-white/80 dark:bg-black/80 rounded-full h-6 w-6 flex items-center justify-center"
                            title={customThumbnailPreview ? "Change thumbnail" : "Add thumbnail"}
                        >
                            {customThumbnailPreview ? (
                                <img src={customThumbnailPreview} alt="thumb" className="h-full w-full object-cover rounded-full" />
                            ) : (
                                <svg className="w-3.5 h-3.5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2" />
                                    <path d="M3 9l4-4 4 4 4-6 4 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
                                </svg>
                            )}
                        </button>
                        {customThumbnailPreview && (
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onRemoveThumbnail?.(); }}
                                className="bg-red-500/80 rounded-full h-6 w-6 flex items-center justify-center text-white text-[11px] font-bold"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}
            </div>
        ) : (
            <div className="h-[15rem] w-[9rem] mt-20 rounded-lg bg-gray-800 flex items-center justify-center">
                <p className="text-white text-sm">No recording available</p>
            </div>
        );
    }

    // Step 2: Full screen video preview or Edit mode or Text mode
    if (step === 2) {
        // Text mode: Portrait view with text overlay
        if (isTextMode) {
            const textStyles = [
                { fontSize: '24px', fontWeight: 'bold', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Arial, sans-serif' },
                { fontSize: '22px', fontWeight: '600', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Georgia, serif' },
                { fontSize: '24px', fontWeight: 'bold', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Helvetica, Arial, sans-serif' },
                { fontSize: '22px', fontWeight: '500', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Times New Roman, serif' },
                { fontSize: '20px', fontWeight: 'bold', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Verdana, sans-serif' },
                { fontSize: '20px', fontWeight: '600', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Courier New, monospace' },
                { fontSize: '24px', fontWeight: '900', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Impact, Arial Black, sans-serif' },
                { fontSize: '22px', fontWeight: '600', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Comic Sans MS, cursive' },
                { fontSize: '22px', fontWeight: '600', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Trebuchet MS, sans-serif' },
                { fontSize: '22px', fontWeight: '500', color: '#ffffff', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', fontFamily: 'Palatino, serif' }
            ];

            return (
                <div className="flex justify-center items-start h-full w-full">
                    <div className="relative flex justify-center items-center">
                        {recordedVideoUrl ? (
                            <video
                                muted
                                // autoPlay
                                ref={videoRef}
                                loop
                                playsInline
                                src={recordedVideoUrl}
                                disablePictureInPicture
                                className="object-cover h-[500px] w-[280px] rounded-sm mt-16"
                                style={{ aspectRatio: '9/16' }}
                            />
                        ) : (
                            <video
                                muted
                                autoPlay
                                playsInline
                                ref={videoRef}
                                disablePictureInPicture
                                className="object-cover h-[500px] w-[280px] rounded-lg"
                                style={{ aspectRatio: '9/16' }}
                            />
                        )}

                        {/* Text input overlay in center when in text mode */}
                        {isTextMode && (
                            <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-center px-4">
                                <input
                                    type="text"
                                    value={textInput}
                                    onChange={(e) => setTextInput(e.target.value)}
                                    placeholder="Text"
                                    className="w-fit text-center p-2 rounded-lg text-white placeholder-gray-500 focus:outline-none bg-gray-900/70 backdrop-blur-sm"
                                    autoFocus
                                />
                            </div>
                        )}

                        {/* Text overlay */}
                        {textInput && !isTextMode && (
                            <div
                                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center px-4 py-2 rounded select-none pointer-events-none max-w-[260px]"
                                style={textStyles[selectedTextStyle]}
                            >
                                {textInput}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // Edit mode: Portrait view
        if (isEditMode) {
            return (
                <div className="flex justify-center items-center h-full w-full">
                    {recordedVideoUrl ? (
                        <video
                            ref={videoRef}
                            muted
                            autoPlay
                            loop
                            playsInline
                            src={recordedVideoUrl}
                            disablePictureInPicture
                            className="object-cover h-[70%] w-[80%] max-w-[400px] rounded-lg"
                            style={{ aspectRatio: '9/16' }}
                        />
                    ) : (
                        <video
                            muted
                            autoPlay
                            playsInline
                            ref={videoRef}
                            disablePictureInPicture
                            className="object-cover h-[70%] w-[60%] max-w-[400px] rounded-lg"
                            style={{ aspectRatio: '9/16' }}
                        />
                    )}
                </div>
            );
        }

        // Normal mode: Full screen
        return (
            <div className="relative w-full h-full">
                {recordedVideoUrl ? (
                    <video
                        ref={videoRef}
                        muted
                        autoPlay
                        loop
                        playsInline
                        src={recordedVideoUrl}
                        disablePictureInPicture
                        className="object-cover h-full w-full"
                    />
                ) : (
                    <video
                        muted
                        autoPlay
                        playsInline
                        ref={videoRef}
                        disablePictureInPicture
                        className="object-cover h-full w-full"
                    />
                )}
            </div>
        );
    }

    // Step 1: Live camera feed
    return (
        <video
            muted
            autoPlay
            playsInline
            ref={videoRef}
            disablePictureInPicture
            className="object-cover h-full w-full"
            style={filterStyle}
        />
    );
};

export default CameraPreview;